import { useMutation } from "@tanstack/react-query";
import { t } from "i18next";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../contexts/RequestsContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { backendService } from "../../services/backend.service";
import type { BulkNurseJobsApplyRequest, BulkNurseJobsPreview, BulkNurseJobsRequest, BulkNurseJobsResult, BulkNurseJobsSkipped } from "../../utils/models";

/** Lets the Team page see an in-flight apply (`useIsMutating`) without owning the mutation itself. */
export const BULK_NURSE_JOBS_APPLY_MUTATION_KEY = ["team-bulk-nurse-jobs-apply"] as const;

const errorMessage = (error: unknown): string => (error instanceof Error ? error.message : String(error));

/**
 * The write went through but re-reading the roster didn't. Kept apart from a failed write because the
 * two need opposite answers: this one must NOT say "couldn't change jobs" (they changed), and the
 * dialog must close rather than invite a second apply. Carries the server's result so the page can
 * still act on it (re-select who failed).
 */
export class BulkJobsRefreshError extends Error {
	readonly result: BulkNurseJobsResult;

	constructor(cause: unknown, result: BulkNurseJobsResult) {
		super(errorMessage(cause));
		this.name = "BulkJobsRefreshError";
		this.result = result;
	}
}

export const countSkipped = (skipped: BulkNurseJobsSkipped): number =>
	skipped.notInDepartment.length + skipped.notHomeFacility.length + skipped.alreadyHasJob.length + skipped.doesNotHoldJob.length;

/**
 * One line for the whole batch: what changed, then every reason something didn't. Each skip reason is
 * its own clause — "can't change them from here" and "they already had it" are unrelated, and folding
 * them together would claim one when it was the other.
 */
export const buildBulkJobsSummary = (result: BulkNurseJobsResult): string => {
	const { skipped } = result;
	const parts = [t("team.bulk.jobs.done", { count: result.updated.length })];
	if (result.blockedByShifts.length > 0) parts.push(t("team.bulk.jobs.left_unchanged", { count: result.blockedByShifts.length }));
	if (skipped.notHomeFacility.length > 0) parts.push(t("team.bulk.skipped_home_facility", { count: skipped.notHomeFacility.length }));
	if (skipped.alreadyHasJob.length > 0) parts.push(t("team.bulk.jobs.skipped_already_has_job", { count: skipped.alreadyHasJob.length }));
	if (skipped.doesNotHoldJob.length > 0) parts.push(t("team.bulk.jobs.skipped_does_not_hold_job", { count: skipped.doesNotHoldJob.length }));
	if (skipped.notInDepartment.length > 0) parts.push(t("team.bulk.jobs.skipped_not_in_department", { count: skipped.notInDepartment.length }));
	if (result.failed.length > 0) parts.push(t("team.bulk.failed", { count: result.failed.length }));
	return parts.join(" · ");
};

/**
 * Bulk "add a job" / "replace a job" for the Team page selection: a dry-run `preview` that powers the
 * review step, and the `apply` that commits it. Unlike activate/deactivate this is ONE request — the
 * server loops the batch — so the only fan-out here is the refresh afterwards.
 */
export const useBulkNurseJobs = () => {
	const { chosenDepartment, refreshNursesData, refreshDepartmentData, showErrorSnackbar } = useHospitalData();
	const { refreshRequests } = useRequestsContext();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const departmentId = chosenDepartment.department_id;

	const preview = useMutation<BulkNurseJobsPreview, Error, BulkNurseJobsRequest>({
		mutationFn: async (body) => {
			track("team_bulk_jobs_preview_start", { department_id: departmentId, action: body.action, count: body.nurseIds.length });
			const { data } = await backendService.previewBulkNurseJobs(departmentId, body);
			return data;
		},
		onSuccess: (data, body) => {
			track("team_bulk_jobs_preview_success", {
				department_id: departmentId,
				action: body.action,
				will_change_count: data.willChange.length,
				blocked_count: data.blockedByShifts.length,
				skipped_count: countSkipped(data.skipped),
			});
		},
		onError: (error, body) => {
			trackError("team_bulk_jobs_preview_error", { department_id: departmentId, action: body.action, error_message: errorMessage(error) });
			showErrorSnackbar(t("team.bulk.jobs.preview.error"), { department_id: departmentId }, error);
		},
	});

	const apply = useMutation<BulkNurseJobsResult, Error, BulkNurseJobsApplyRequest>({
		mutationKey: BULK_NURSE_JOBS_APPLY_MUTATION_KEY,
		mutationFn: async (body) => {
			track("team_bulk_jobs_apply_start", { department_id: departmentId, action: body.action, blocked_mode: body.blockedMode, count: body.nurseIds.length });
			const { data } = await backendService.updateBulkNurseJobs(departmentId, body);
			// Part of the operation, not a follow-up: a replaced job cancels pending pickups and moves shifts
			// server-side, so the roster, the requests and the schedule grid all have to be re-read before
			// the batch can honestly be reported done.
			try {
				await Promise.all([refreshNursesData(departmentId), refreshRequests(), refreshDepartmentData(departmentId, false)]);
			} catch (error) {
				throw new BulkJobsRefreshError(error, data);
			}
			return data;
		},
		onSuccess: (result, body) => {
			const counts = {
				department_id: departmentId,
				action: body.action,
				blocked_mode: body.blockedMode,
				updated_count: result.updated.length,
				left_unchanged_count: result.blockedByShifts.length,
				skipped_count: countSkipped(result.skipped),
			};
			if (result.failed.length > 0) {
				trackError("team_bulk_jobs_apply_partial_failure", { ...counts, failed_count: result.failed.length });
			} else {
				track("team_bulk_jobs_apply_success", counts);
			}
			showSnackbar(buildBulkJobsSummary(result), result.failed.length > 0 ? "warning" : "success");
		},
		onError: (error, body) => {
			const isRefreshError = error instanceof BulkJobsRefreshError;
			trackError("team_bulk_jobs_apply_error", {
				department_id: departmentId,
				action: body.action,
				blocked_mode: body.blockedMode,
				stage: isRefreshError ? "refresh" : "update",
				error_message: errorMessage(error),
			});
			if (isRefreshError) {
				// The write landed, so the admin still needs its outcome — including why any failed staff stay selected.
				showSnackbar(`${t("team.bulk.jobs.refresh_error")} ${buildBulkJobsSummary(error.result)}`, "error");
				return;
			}
			showErrorSnackbar(t("team.bulk.jobs.apply.error"), { department_id: departmentId }, error);
		},
	});

	return { preview, apply };
};
